import { useState, useRef, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Play, Square, MousePointer2, Smartphone } from 'lucide-react';

type DemoStep = {
    type: 'wait' | 'scroll' | 'cursor' | 'click' | 'hover';
    target?: string;
    offset?: number;
    duration: number; // Duration is always good to have, though optional in logic, we set defaults
};

export default function DemoController() {
    const [searchParams] = useSearchParams();
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    // Ref to track playing state inside async loop without closure staleness
    const isPlayingRef = useRef(false);

    const cursorControls = useAnimation();

    // Only active if ?demo=true is in URL
    if (searchParams.get('demo') !== 'true') return null;

    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Handle Mobile Mode Styles
    useEffect(() => {
        if (isMobile) {
            // Apply Mobile Styles to Body/Root
            document.body.style.backgroundColor = '#1a1a1a'; // Dark Studio BG

            const root = document.getElementById('root');
            if (root) {
                root.style.maxWidth = '390px'; // iPhone 13/14 Width
                root.style.margin = '40px auto';
                root.style.backgroundColor = '#ffffff';
                root.style.minHeight = '100vh';
                root.style.boxShadow = '0 0 0 10px #333, 0 25px 50px -12px rgba(0, 0, 0, 0.5)'; // Frame + Shadow
                root.style.borderRadius = '40px';
                root.style.overflow = 'hidden'; // Rounded corners for content
                root.style.transition = 'all 0.5s ease';
            }
        } else {
            // Revert Styles
            document.body.style.backgroundColor = '';

            const root = document.getElementById('root');
            if (root) {
                root.style.maxWidth = '';
                root.style.margin = '';
                root.style.backgroundColor = '';
                root.style.minHeight = '';
                root.style.boxShadow = '';
                root.style.borderRadius = '';
                root.style.overflow = '';
                root.style.transition = 'all 0.5s ease';
            }
        }
    }, [isMobile]);

    const runDemo = async () => {
        if (isPlayingRef.current) return;

        setIsPlaying(true);
        isPlayingRef.current = true;

        // Sequence of actions
        const steps: DemoStep[] = [
            { type: 'wait', duration: 1000 },
            // Hero Section
            { type: 'scroll', target: '#home-hero', offset: 0, duration: 1000 },
            { type: 'cursor', target: '#btn-hero-cta', duration: 1500 },
            { type: 'hover', target: '#btn-hero-cta', duration: 500 },
            { type: 'wait', duration: 1000 },

            // Logos
            { type: 'scroll', target: '#home-logos', offset: -100, duration: 1000 },
            { type: 'wait', duration: 2000 },

            // Features
            { type: 'scroll', target: '#home-features', offset: -50, duration: 1000 },
            { type: 'cursor', target: '#home-features h2', duration: 1500 },
            { type: 'wait', duration: 3000 },

            // Benefits
            { type: 'scroll', target: '#home-benefits', offset: -50, duration: 1000 },
            { type: 'wait', duration: 3000 },

            // About
            { type: 'scroll', target: '#home-about', offset: -50, duration: 1000 },
            { type: 'wait', duration: 3000 },

            // FAQ
            { type: 'scroll', target: '#home-faq', offset: -50, duration: 1000 },
            { type: 'wait', duration: 3000 },

            // Back to Call to Action
            { type: 'scroll', target: '#home-hero', offset: 0, duration: 1500 },
            { type: 'cursor', target: '#btn-hero-cta', duration: 1000 },
            { type: 'click', target: '#btn-hero-cta', duration: 500 },
        ];

        // Reset scroll gently
        window.scrollTo({ top: 0, behavior: 'smooth' });
        await wait(1000);

        for (const step of steps) {
            if (!isPlayingRef.current) break;

            if (step.type === 'wait') {
                await wait(step.duration);
            }
            else if (step.type === 'scroll' && step.target) {
                const element = document.querySelector(step.target);
                if (element) {
                    const headerOffset = 80; // Approximate header height
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.scrollY + (step.offset || 0) - headerOffset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: "smooth"
                    });
                    // Wait for scroll to (mostly) finish.
                    // Smooth scroll duration is browser-dependent, so we give it a fixed generic buffer or user-defined duration.
                    await wait(step.duration || 1000);
                }
            }
            else if (step.type === 'cursor' && step.target) {
                const element = document.querySelector(step.target);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    // Move to center
                    const x = rect.left + (rect.width / 2);
                    const y = rect.top + (rect.height / 2);

                    await cursorControls.start({
                        x: x,
                        y: y,
                        opacity: 1,
                        transition: { duration: step.duration ? step.duration / 1000 : 1 }
                    });
                }
            }
            else if (step.type === 'hover') {
                // Simulate hover effect if possible (hard with just JS, but we can visualize it)
                await cursorControls.start({
                    scale: 0.9,
                    transition: { duration: 0.2 }
                });
                await cursorControls.start({
                    scale: 1,
                    transition: { duration: 0.2 }
                });
            }
            else if (step.type === 'click') {
                await cursorControls.start({
                    scale: 0.8,
                    transition: { duration: 0.1 }
                });
                await cursorControls.start({
                    scale: 1,
                    transition: { duration: 0.1 }
                });
                // Optional: Actually click?
                // document.querySelector(step.target)?.click();
            }
        }

        setIsPlaying(false);
        isPlayingRef.current = false;
    };

    const stopDemo = () => {
        setIsPlaying(false);
        isPlayingRef.current = false;
        cursorControls.start({ opacity: 0 });
    };

    return (
        <div style={{ position: 'relative', zIndex: 99999 }}>
            {/* Control Panel */}
            <div className="fixed bottom-4 right-4 bg-white/90 backdrop-blur shadow-2xl rounded-xl p-2 flex gap-2 border border-sky-100 z-[99999]">
                <button
                    onClick={() => setIsMobile(!isMobile)}
                    className={`p-2 rounded-lg font-medium transition-all ${isMobile ? 'bg-sky-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                    title={isMobile ? "Vista Desktop" : "Vista Móvil"}
                >
                    <Smartphone size={20} />
                </button>
                <div className="w-px bg-gray-300 mx-1"></div>
                {!isPlaying ? (
                    <button
                        onClick={runDemo}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                    >
                        <Play size={18} fill="currentColor" />
                        <span>Demo</span>
                    </button>
                ) : (
                    <button
                        onClick={stopDemo}
                        className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all"
                    >
                        <Square size={18} fill="currentColor" />
                        <span>Stop</span>
                    </button>
                )}
            </div>

            {/* Fake Cursor */}
            <motion.div
                initial={{ opacity: 0, x: 0, y: 0 }}
                animate={cursorControls}
                className="fixed top-0 left-0 pointer-events-none z-[100000]"
                style={{
                    marginTop: -5, // Tip adjustment
                    marginLeft: -5
                }}
            >
                <div className="relative drop-shadow-2xl">
                    <MousePointer2
                        className="w-12 h-12 text-black fill-white"
                        strokeWidth={1}
                    />
                    {/* Optional click ripple */}
                    {/* <div className="absolute top-0 left-0 w-full h-full bg-sky-400 rounded-full opacity-0" /> */}
                </div>
            </motion.div>
        </div>
    );
}
